// /app/frontend/src/components/frugal-v2/components/BankEmailModal.jsx
// Modal to display and manage the user's bank forwarding email (150 lines)

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Mail, Copy, RefreshCw, AlertTriangle, Check, X, CreditCard, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const BankEmailModal = ({ isOpen, onClose, onUpgrade, t }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchBankEmail();
    }
  }, [isOpen]);

  const fetchBankEmail = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/v2/frugal/emails/bank/forwarding-email`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching bank email:', error);
      toast.error('Error al cargar email de banco');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (data?.forwarding_email) {
      navigator.clipboard.writeText(data.forwarding_email);
      setCopied(true);
      toast.success(t('frugal.bankEmail.copied', 'Email copiado'));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm(t('frugal.bankEmail.regenerateConfirm', '¿Estás seguro? El email anterior dejará de funcionar.'))) {
      return;
    }

    setRegenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/v2/frugal/emails/bank/regenerate-token`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
        toast.success(t('frugal.bankEmail.regenerated', 'Email regenerado'));
      }
    } catch (error) {
      console.error('Error regenerating token:', error);
      toast.error('Error al regenerar email');
    } finally {
      setRegenerating(false);
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="w-full max-w-md mx-4 bg-slate-800 rounded-2xl p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto" />
        </div>
      </div>,
      document.body
    );
  }

  const isExpired = data?.subscription_status === 'expired';
  const showWarning = data?.show_trial_warning && !isExpired;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md mx-4 bg-slate-800 rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Mail className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {t('frugal.bankEmail.title', 'Email para Bancos')}
              </h3>
              <p className="text-xs text-slate-400">
                {t('frugal.bankEmail.subtitle', 'Configura este email en tu banco')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Trial Warning */}
          {showWarning && (
            <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-400 font-medium">
                  {t('frugal.bankEmail.trialWarning', 'Tu trial vence pronto')}
                </p>
                <p className="text-xs text-slate-400">
                  {t('frugal.bankEmail.daysRemaining', { days: data?.days_remaining }, `Quedan ${data?.days_remaining} días`)}
                </p>
              </div>
            </div>
          )}

          {/* Expired State */}
          {isExpired ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
              <h4 className="text-lg font-medium text-white mb-2">
                {t('frugal.bankEmail.expired', 'Trial Expirado')}
              </h4>
              <p className="text-sm text-slate-400 mb-4">
                {t('frugal.bankEmail.expiredDesc', 'Activa tu suscripción para continuar recibiendo alertas de tu banco.')}
              </p>
              <button
                onClick={() => onUpgrade?.('monthly')}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg flex items-center justify-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                {t('frugal.subscription.upgrade', 'Activar por $2.99/mes')}
              </button>
            </div>
          ) : (
            <>
              {/* Email Display */}
              <div className="bg-slate-900/50 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">
                  {t('frugal.bankEmail.yourEmail', 'Tu email único')}
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-emerald-400 text-sm font-mono break-all">
                    {data?.forwarding_email}
                  </code>
                  <button
                    onClick={handleCopy}
                    className={`p-2 rounded-lg transition-colors ${
                      copied ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-slate-400 hover:text-white'
                    }`}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Instructions */}
              <div className="text-sm text-slate-400 space-y-2">
                <p>{data?.instructions?.es || 'Configura este email en las alertas de tu banco.'}</p>
                <p className="text-xs text-slate-500">
                  {t('frugal.bankEmail.howTo', 'Los emails recibidos aparecerán en tu pestaña de Emails para aprobar.')}
                </p>
              </div>

              {/* Regenerate Button */}
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="w-full py-2 text-sm text-slate-400 hover:text-white flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
                {t('frugal.bankEmail.regenerate', 'Regenerar email (por seguridad)')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BankEmailModal;
